import * as Yup from 'yup';
import {
  phoneNumberValue,
  validatePhoneNumberForFormik,
} from '../../../utils/helper';
import { getfloristId } from '../../../utils/localStorage';

export const userSchema = Yup.object().shape({
  firstName: Yup.string()
    .matches(/^[A-Za-z]+$/, 'First name can only contain letters')
    .min(2, 'First name must be at least 2 characters')
    .max(100, 'First name cannot exceed 100 characters')
    .required('First name is required'),

  lastName: Yup.string()
    .matches(/^[A-Za-z]+$/, 'Last name can only contain letters')
    .min(2, 'Last name must be at least 2 characters')
    .max(100, 'Last name cannot exceed 100 characters')
    .required('Last name is required'),

  jobTitle: Yup.string()
    .min(2, 'Job title must be at least 2 characters')
    .max(100, 'Job title cannot exceed 100 characters')
    .required('Job title is required'),

  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),

  phone: Yup.string()
    .required('Phone number is required')
    .min(6, 'Invalid number'), // Ensures phone number has more than 5 digits
});

export const initialuserForm = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  jobTitle: '',
  roleId: '',
  address: '',
  city: '',
  country: '',
  zipCode: '',
  state: '',
  imageUrl: '',
  latitude: '',
  longitude: '',
  emailVerified: false,
  fullName: '',
  password: '',
};

export const initialPolicyForm = {
  policy: '',
  details: '',
  floristId: '',
};

export const policySchema = Yup.object().shape({
  policy: Yup.string().required('Policy name is required'),
  details: Yup.string().required('Description is required'),
});

export const initialAddBankForm = {
  bankName: '',
  accountHolder: '',
  accountNo: '',
  iban: '',
  swiftCode: '',
  floristId: getfloristId,
};

export const addBankSchema = Yup.object().shape({
  bankName: Yup.string().required('Bank name is required'),
  accountHolder: Yup.string().required('Account holder name is required'),
  accountNo: Yup.string()
    .required('Account number is required')
    .matches(phoneNumberValue, 'Account number is not valid'),
  iban: Yup.string().required('IBAN is required'),
  swiftCode: Yup.string().required('Swift code is required'),
});

export const floriestForm = {
  companyName: '',
  address: '',
  city: '',
  state: '',
  zipCode: '',
  country: '',
};

export const floristSchema = Yup.object().shape({
  companyName: Yup.string().nullable(true).required('Company name is required'),
  address: Yup.string().required('Address is required').nullable(),
  contactUsEmail: Yup.string().nullable(true),
  contactNo: Yup.string().nullable(true),
  city: Yup.string().nullable(true),
  state: Yup.string().nullable(true),
  zipCode: Yup.string().nullable(true),
  country: Yup.string().nullable(true),
});

export const onboardingSchemaStepOne = Yup.object().shape({
  companyName: Yup.string().required('Company name is required').nullable(),
  address: Yup.string().required('Address is required').nullable(),
  contactNo: Yup.string()
    .required('Phone number is required')
    .test(
      'Phone Format Validation',
      'Please enter a valid phone number.',
      validatePhoneNumberForFormik,
    )
    .nullable(),
  contactUsEmail: Yup.string()
    .email('Invalid email address')
    .required('Email is required')
    .nullable(),
});

export const onboardingSchemaStepTwo = Yup.object().shape({
  floralServicesList: Yup.array()
    .of(Yup.object())
    .min(1, 'Services is required'),
});

export const roleSchema = Yup.object().shape({
  name: Yup.string().required('Role name is required'),
});

export const initialRoleForm = {
  name: '',
  florist: getfloristId,
  resources: '',
};

export const shopSettingSchema = Yup.object().shape({
  zipCodeDeliveryChargeDTOS: Yup.array()
    .of(
      Yup.lazy(() =>
        Yup.object().shape({
          zipCode: Yup.string().required('Zip code is required'),
          deliveryCharges: Yup.string().required('Delivery fee is required'),
        }),
      ),
    )
    .test('is-unique', 'Zip code must be unique', function (value) {
      if (!value?.length) return true;
      const zipCodes = value.map((item) => item?.zipCode);
      const isUnique = new Set(zipCodes).size === zipCodes.length;
      if (!isUnique) {
        const errorObj = zipCodes.reduce((errors, zipCode, index) => {
          if (zipCodes.indexOf(zipCode) !== index) {
            errors[index] = { zipCode: 'Zip code must be unique' };
          }
          return errors;
        }, {});
        return this.createError({
          path: `zipCodeDeliveryChargeDTOS`,
          message: errorObj,
        });
      }
      return true;
    })
    .nullable(),
  distanceBasedDeliveryCharges: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required('Name is required'),
        from: Yup.number().required('From is required'),
        to: Yup.number()
          .required('To is required')
          .when('from', (from, schema) => {
            return schema.min(from, 'To must be greater than From');
          }),
        deliveryCharges: Yup.string().required('Delivery charges are required'),
      }),
    )
    .test('no-overlapping-ranges', 'Ranges cannot overlap', function (value) {
      if (!value?.length) return true;
      const ranges = value.map((item) => [item.from, item.to]);
      const errors = {};
      for (let i = 0; i < ranges.length; i++) {
        for (let j = i + 1; j < ranges.length; j++) {
          const [from1, to1] = ranges[i];
          const [from2, to2] = ranges[j];
          if (!(from1 >= to2 || to1 <= from2)) {
            if (!errors[i]) {
              errors[i] = {};
            }
            if (!errors[j]) {
              errors[j] = {};
            }
            errors[i].from = 'Overlap range not allowed';
            errors[j].from = 'Overlap range not allowed';
            errors[i].to = 'Overlap range not allowed';
            errors[j].to = 'Overlap range not allowed';
          }
        }
      }
      return Object.keys(errors).length === 0
        ? true
        : this.createError({
            path: `distanceBasedDeliveryCharges`,
            message: errors,
          });
    })
    .nullable(),
});

export const nearByPlaceDTOSSchema = Yup.object().shape({
  name: Yup.string().nullable(true).required('Name is required'),
  type: Yup.string().nullable(true).required('Type is required'),
  address1: Yup.string().nullable(true).required('Address is required'),
  city: Yup.string().nullable(true).required('City is required'),
  zipCode: Yup.string().nullable(true).required('ZipCode is required'),
  state: Yup.string().nullable(true).required('state is required'),
});
